import { z } from 'zod';

// Schema deliberately omits VITE_FIREBASE_STORAGE_BUCKET — we use Firebase
// for Phone Auth + FCM only. File uploads go to our own /v1/* endpoints
// (base64-in-DB pattern, see GH#98). Adding the field would force ops to
// provision an env var that's never read.
const EnvSchema = z.object({
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_VAPID_KEY: z.string().min(1),
  VITE_API_BASE: z.string().url(),
});

export type Env = z.infer<typeof EnvSchema>;

let _env: Env | null = null;

/**
 * Returns the validated Vite env. Call inside a function or component —
 * NEVER at module top level. A top-level call evaluates during the Vite
 * module-load phase (before React mounts), so a thrown "missing env" error
 * bypasses any error boundary and produces a white screen. Inside a render
 * (or a function called after mount) the throw can be caught and surfaced.
 */
export function env(): Env {
  if (_env) return _env;
  const parsed = EnvSchema.safeParse(import.meta.env);
  if (!parsed.success) {
    // surface in console + UI splash, don't crash the bundle
    // eslint-disable-next-line no-console
    console.error('Invalid VITE_* env:', parsed.error.flatten().fieldErrors);
    throw new Error('App misconfigured: missing VITE_* env vars (check apps/driver/.env)');
  }
  _env = parsed.data;
  return _env;
}
